import type { Coordinates } from '../types/location';

export interface GeolocationResult {
  coords: Coordinates;
  accuracy: number;
  timestamp: number;
}

/**
 * Get current position using the Web Geolocation API (navigator.geolocation).
 *
 * The Capacitor native WebView bridges navigator.geolocation to the OS-level
 * provider (FusedLocationProviderClient on Android, CLLocationManager on iOS),
 * which returns cached positions within maximumAge almost instantly. In practice
 * this is faster than importing the @capacitor/geolocation plugin on every call.
 * The plugin is still used for PERMISSION checks only (see locationStore).
 */
export function getCurrentPosition(): Promise<GeolocationResult> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({
          coords: { lat: pos.coords.latitude, lng: pos.coords.longitude },
          accuracy: pos.coords.accuracy,
          timestamp: pos.timestamp,
        });
      },
      (err) => reject(err),
      {
        enableHighAccuracy: true,
        timeout: 10_000,
        maximumAge: 30_000,
      },
    );
  });
}

/** Watch position changes. Returns a cleanup function. */
export function watchPosition(
  onUpdate: (result: GeolocationResult) => void,
  onError?: (error: GeolocationPositionError) => void,
): () => void {
  if (!navigator.geolocation) {
    onError?.(
      { code: 2, message: 'Geolocation not supported', PERMISSION_DENIED: 1, POSITION_UNAVAILABLE: 2, TIMEOUT: 3 } as GeolocationPositionError,
    );
    return () => {};
  }

  const id = navigator.geolocation.watchPosition(
    (pos) => {
      onUpdate({
        coords: { lat: pos.coords.latitude, lng: pos.coords.longitude },
        accuracy: pos.coords.accuracy,
        timestamp: pos.timestamp,
      });
    },
    onError,
    {
      enableHighAccuracy: true,
      timeout: 15_000,
      maximumAge: 30_000,
    },
  );

  return () => navigator.geolocation.clearWatch(id);
}
